import RouteError from '@/components/ui/route-error';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useEffect, useMemo, useRef } from 'react';
import { DICTIONARY } from '@/config/dictionary';
import { queryClient } from '@/lib/query-client';
import { useAppStore } from '@/stores/app-store';
import { requireAuth } from '@/utils/require-auth-before-load';
import { APP_PATHS } from '@/config/routes/app-paths';
import { ensureValidPath } from '@/utils/validate-path';
import toSlugId from '@/utils/to-slug-id';
import findCreatedCatalogId from '@/utils/find-created-catalog-id';
import {
  getServiceFeatureCatalogQueryOptions,
  useServiceFeatureCatalog,
  useUpdateServiceFeatureCatalog,
} from '@/hooks/use-service-feature-catalog';
import {
  getAdminServicesConfigQueryOptions,
  useAdminServicesConfig,
  useUpdateAdminServicesConfig,
} from '@/features/admin-settings/api/admin-api';
import CatalogItemEditorShell from '@/features/admin-settings/components/catalog-item-editor-shell';
import CatalogItemEditorForm from '@/features/admin-settings/components/catalog-item-editor-form';
import {
  catalogItemEditorSchema,
  type CatalogItemEditorFormValues,
} from '@/features/admin-settings/types/form-schemas';
import type { ServiceDefinition } from '@/types/services';

const ServiceEditor = () => {
  const navigate = useNavigate();
  const { serviceId } = Route.useParams();
  const isCreate = serviceId === 'new';

  const d = DICTIONARY.adminSettingsPage.serviceAdmin.editor;

  const { data: catalog, isLoading, refetch: refetchCatalog } = useServiceFeatureCatalog();
  const { data: adminConfig } = useAdminServicesConfig();
  const { mutate: updateCatalog, isPending: isSavingCatalog } = useUpdateServiceFeatureCatalog();
  const { mutate: updateAdminConfig, isPending: isSavingAdminConfig } =
    useUpdateAdminServicesConfig();

  const existingService = useMemo(
    () => catalog?.services.find((service) => service.id === serviceId),
    [catalog, serviceId],
  );

  const initialFormValues = useMemo<CatalogItemEditorFormValues>(() => {
    if (!isCreate && existingService != null) {
      return {
        categoryId: existingService.categoryId,
        newCategoryLabel: '',
        name: existingService.name,
        description: existingService.description,
        tcapId: existingService.tcapId ?? '',
        assetType: existingService.assetType ?? 'external-url',
        assetLocation: existingService.assetLocation ?? existingService.learnMoreHref ?? '',
        shortDescription: existingService.shortDescription ?? '',
        longDescription: existingService.longDescription ?? '',
      };
    }

    return {
      categoryId: catalog?.serviceCategories[0]?.id ?? '',
      newCategoryLabel: '',
      name: '',
      description: '',
      tcapId: '',
      assetType: 'external-url',
      assetLocation: '',
      shortDescription: '',
      longDescription: '',
    };
  }, [catalog, existingService, isCreate]);

  const isSaving = isSavingCatalog || isSavingAdminConfig;

  const goBack = () => {
    void navigate({
      to: ensureValidPath(APP_PATHS.adminSettings),
      search: { tab: 'service-admin' },
    });
  };

  const saveService = (value: CatalogItemEditorFormValues) => {
    if (catalog == null) return;

    const trimmedName = value.name.trim();
    if (trimmedName.length === 0) return;

    const hasNewCategory = value.newCategoryLabel.trim().length > 0;
    const resolvedCategoryId = hasNewCategory
      ? toSlugId(value.newCategoryLabel, 'service-group')
      : value.categoryId;

    if (resolvedCategoryId.trim().length === 0) return;

    const existingCategories = catalog.serviceCategories;
    const categories = hasNewCategory
      ? existingCategories.some((category) => category.id === resolvedCategoryId)
        ? existingCategories
        : [
            ...existingCategories,
            {
              id: resolvedCategoryId,
              label: value.newCategoryLabel.trim(),
            },
          ]
      : existingCategories;

    const idBase = toSlugId(value.tcapId, toSlugId(trimmedName, 'service'));
    const existingIds = new Set(catalog.services.map((service) => service.id));

    let nextId = serviceId;
    if (isCreate) {
      nextId = idBase;
      if (existingIds.has(nextId)) {
        nextId = `${idBase}-${String(Date.now())}`;
      }
    }

    const nextService: ServiceDefinition = {
      id: nextId,
      name: trimmedName,
      description: value.description.trim(),
      categoryId: resolvedCategoryId,
      learnMoreHref: value.assetLocation.trim().length > 0 ? value.assetLocation.trim() : undefined,
      tcapId: value.tcapId.trim().length > 0 ? value.tcapId.trim() : undefined,
      assetType: value.assetType,
      assetLocation: value.assetLocation.trim().length > 0 ? value.assetLocation.trim() : undefined,
      shortDescription:
        value.shortDescription.trim().length > 0 ? value.shortDescription.trim() : undefined,
      longDescription:
        value.longDescription.trim().length > 0 ? value.longDescription.trim() : undefined,
      requiresCountry: existingService?.requiresCountry,
      additionalPrice: existingService?.additionalPrice,
      // `PUT /services/catalog` rejects any service without a `visibility`, so an edit has to
      // carry the platform value back. A new service has none yet; the follow-up
      // `updateAdminConfig` below is what actually sets it.
      visibility: existingService?.visibility ?? 'on',
    };

    const services = isCreate
      ? [...catalog.services, nextService]
      : catalog.services.map((service) => (service.id === serviceId ? nextService : service));

    updateCatalog(
      { ...catalog, serviceCategories: categories, services },
      {
        onSuccess: (savedCatalog) => {
          if (!isCreate || adminConfig == null) {
            goBack();
            return;
          }

          // `nextId` was only ever a placeholder: the save mints the real id server-side, and
          // sending the placeholder on to services-config fails with "Unknown service or
          // feature ids". The saved catalog carries the id the service actually got.
          const createdId = findCreatedCatalogId(existingIds, savedCatalog.services, nextId);

          const hasEntry = adminConfig.serviceConfigs.some(
            (config) => config.serviceId === createdId,
          );
          if (hasEntry) {
            goBack();
            return;
          }

          updateAdminConfig(
            {
              serviceConfigs: [
                ...adminConfig.serviceConfigs,
                { serviceId: createdId, visibility: 'on' },
              ],
              featureConfigs: adminConfig.featureConfigs,
            },
            // The service itself is saved by this point. Holding the user on the form after a
            // failed visibility update invites a second submit, which would create a duplicate
            // service, so leave either way and let the error toast carry the bad news.
            { onSuccess: goBack, onError: goBack },
          );
        },
      },
    );
  };

  const form = useForm({
    defaultValues: initialFormValues,
    onSubmit: ({ value }) => {
      const parsedValue = catalogItemEditorSchema.parse(value);
      saveService(parsedValue);
    },
  });

  // Data is prefetched by the route loader, so `initialFormValues` is normally
  // already correct on first render. This one-shot reset covers the edge case
  // where the catalog query resolves after mount (matching the ref-guard pattern
  // in src/hooks/use-bootstrap-auth.ts) without turning into a perpetual mirror.
  const hasResetForData = useRef(false);
  useEffect(() => {
    if (hasResetForData.current || catalog == null) return;
    hasResetForData.current = true;
    form.reset(initialFormValues);
  }, [catalog, initialFormValues, form]);

  const categories = (catalog?.serviceCategories ?? []).map((c) => ({
    id: c.id,
    label: c.label,
  }));

  return (
    <form.Subscribe selector={(state) => [state.values, state.isSubmitting] as const}>
      {([values, isSubmitting]) => (
        <CatalogItemEditorShell
          isLoading={isLoading}
          loadFailed={!isLoading && catalog == null}
          onRetry={() => {
            void refetchCatalog();
          }}
          notFound={!isCreate && catalog != null && existingService == null}
          isCreate={isCreate}
          title={isCreate ? d.addTitle : d.editTitle}
          subtitle={isCreate ? d.addSubtitle : d.editSubtitle(existingService?.name ?? '')}
          onBack={goBack}
          onCancel={goBack}
          onSave={() => {
            void form.handleSubmit();
          }}
          isSaving={isSaving || isSubmitting}
          saveDisabled={isSaving || isSubmitting || values.name.trim().length === 0}
          saveLabel={isCreate ? d.saveAdd : DICTIONARY.common.saveChanges}
          backLabel={d.backLabel}
          cancelLabel={DICTIONARY.common.cancel}
        >
          <CatalogItemEditorForm
            strings={d}
            categories={categories}
            categoryId={values.categoryId}
            onCategoryIdChange={(value) => {
              form.setFieldValue('categoryId', value);
            }}
            newCategoryLabel={values.newCategoryLabel}
            onNewCategoryLabelChange={(value) => {
              form.setFieldValue('newCategoryLabel', value);
            }}
            name={values.name}
            onNameChange={(value) => {
              form.setFieldValue('name', value);
            }}
            description={values.description}
            onDescriptionChange={(value) => {
              form.setFieldValue('description', value);
            }}
            tcapId={values.tcapId}
            onTcapIdChange={(value) => {
              form.setFieldValue('tcapId', value);
            }}
            assetType={values.assetType}
            onAssetTypeChange={(value) => {
              form.setFieldValue('assetType', value);
            }}
            assetLocation={values.assetLocation}
            onAssetLocationChange={(value) => {
              form.setFieldValue('assetLocation', value);
            }}
            shortDescription={values.shortDescription}
            onShortDescriptionChange={(value) => {
              form.setFieldValue('shortDescription', value);
            }}
            longDescription={values.longDescription}
            onLongDescriptionChange={(value) => {
              form.setFieldValue('longDescription', value);
            }}
          />
        </CatalogItemEditorShell>
      )}
    </form.Subscribe>
  );
};

export const Route = createFileRoute('/admin-settings/service-admin/$serviceId')({
  beforeLoad: requireAuth,
  loader: async () => {
    const { selectedWholesalerId, currentUser } = useAppStore.getState();
    const wholesalerScope = selectedWholesalerId ?? currentUser?.wholesalers[0]?.id ?? 'default';
    await Promise.allSettled([
      queryClient.prefetchQuery(getServiceFeatureCatalogQueryOptions(wholesalerScope)),
      queryClient.prefetchQuery(getAdminServicesConfigQueryOptions()),
    ]);
    return null;
  },
  component: ServiceEditor,
  errorComponent: RouteError,
});
